import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {useTranslation} from 'react-i18next';

import {NODE_COPY} from '../addNode/nodeCopy';
import NodeTextField from './fields/NodeTextField';

interface NodeIdentityPanelProps {
    nodeType: NodeType | undefined;
    nodeLabel: string;
    setNodeLabel: (label: string) => void;
    readOnly?: boolean;
    /** A band above a full-width editor: label and the type's `dialogLine` side by side. */
    compact?: boolean;
    /** Type-specific controls under the copy line (Send Email: mode and variable mappings). */
    aside?: React.ReactNode;
}

const NodeIdentityPanel = ({
    nodeType,
    nodeLabel,
    setNodeLabel,
    readOnly,
    compact,
    aside
}: NodeIdentityPanelProps) => {
    const {t} = useTranslation();
    const copy = nodeType ? NODE_COPY[nodeType] : undefined;
    // Compact or with an aside, the short `dialogLine` replaces the reference paragraph.
    const line = copy && ((compact || !!aside) ? copy.dialogLine ?? copy.detail : copy.detail);

    const labelField = (
        <NodeTextField
            label={t('Node label')}
            value={nodeLabel}
            onChange={setNodeLabel}
            disabled={readOnly}
        />
    );

    const copyLine = !!line && (
        <Typography variant={'body2'} color={'text.secondary'} sx={{minWidth: 0}}>
            {t(line)}
        </Typography>
    );

    if (compact) {
        return (
            <Stack gap={2}>
                <Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={{sm: 'center'}}>
                    <Stack sx={{width: {xs: '100%', sm: 320}, flexShrink: 0}}>
                        {labelField}
                    </Stack>

                    {copyLine}
                </Stack>

                {aside}
            </Stack>
        );
    }

    return (
        <Stack gap={2.5}>
            {labelField}

            {copyLine}

            {aside}
        </Stack>
    );
};

export default NodeIdentityPanel;
